import type { DataTableQuery } from '../components/DataTable';

/**
 * Meta de paginação retornada pela GeneXus / BFF (camelCase PT-BR).
 * O front normaliza para EN antes de expor às features.
 */
export interface GenexusMetaRaw {
  total: number;
  pagina?: number;
  porPagina?: number;
}

export interface PaginationMeta {
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Traduz `DataTableQuery` (EN) para os query params camelCase PT-BR que o BFF
 * espera (e que ele traduz em PascalCase ao chamar a GeneXus). Mantém só as
 * chaves definidas — chaves vazias/undefined são omitidas.
 *
 * Filtros extras específicos por entidade podem ser mesclados via `extra`.
 */
export function toGenexusParams(
  query: DataTableQuery,
  extra?: Record<string, string | number | boolean | undefined | null>,
): Record<string, string> {
  const params: Record<string, string> = {
    pagina: String(query.page),
    porPagina: String(query.pageSize),
  };
  if (query.search) params.busca = query.search;
  if (query.sortBy) params.ordenarPor = query.sortBy;
  if (query.sortOrder) params.ordem = query.sortOrder;
  if (query.includeInactive) params.incluirInativos = 'true';
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      if (v === undefined || v === null || v === '') continue;
      params[k] = String(v);
    }
  }
  return params;
}

/**
 * Normaliza `meta` do envelope GeneXus (PT-BR) para EN.
 * Defaults conservadores: `page=1`, `pageSize=total` quando ausentes.
 */
export function normalizeMeta(meta: GenexusMetaRaw): PaginationMeta {
  return {
    total: Number(meta.total ?? 0),
    page: Number(meta.pagina ?? 1),
    pageSize: Number(meta.porPagina ?? meta.total ?? 0),
  };
}

/** Envelope de lista pós-normalização (EN). */
export interface ListEnvelope<T> {
  data: T[];
  meta: PaginationMeta;
}

/**
 * Wrapper para casts de envelope vindo da BFF: o adapter já entrega
 * `{data, meta}` — aqui só normalizamos meta para EN.
 */
export function normalizeListEnvelope<T>(envelope: {
  data: T[];
  meta: GenexusMetaRaw;
}): ListEnvelope<T> {
  return { data: envelope.data, meta: normalizeMeta(envelope.meta) };
}
